import { ReactNode, useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { AuthContext } from '../hooks/useAuth'
import { AuthContextType, User, Team, LoginCredentials } from '../types/auth'
import { hasPermission, canAccessTeamData } from '../lib/permissions'
import { api, logger } from '../utils/api'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [team, setTeam] = useState<Team | null>(null)
  const [teams, setTeams] = useState<Team[]>([])
  const [needsOnboarding, setNeedsOnboarding] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing session on mount
    checkAuthState()
  }, [])

  const checkAuthState = async () => {
    return Sentry.startSpan(
      {
        op: 'auth.check_state',
        name: 'Check Auth State'
      },
      async () => {
        try {
          const token = localStorage.getItem('auth_token')
          if (token) {
            logger.debug('Checking auth state')
            const userData = await api.get('/v1/auth/me')
            
            setUser(userData.user)
            setTeam(userData.team)
            setTeams(userData.teams || [])
            setNeedsOnboarding(userData.needsOnboarding || false)
            
            // Set user context in Sentry
            Sentry.setUser({
              id: userData.user.id,
              email: userData.user.email,
              username: userData.user.name
            })
            
            logger.info('Auth state validated', { userId: userData.user.id })
          }
        } catch (error) {
          logger.error('Auth check failed', { error })
          localStorage.removeItem('auth_token')
          Sentry.setUser(null)
        } finally {
          setLoading(false)
        }
      }
    )
  }

  const login = async (credentials: LoginCredentials) => {
    setLoading(true)
    try {
      if (credentials.method === 'sso') {
        // Build WorkOS SSO URL with provider hint
        const params = new URLSearchParams({
          client_id: import.meta.env.VITE_WORKOS_CLIENT_ID,
          redirect_uri: import.meta.env.VITE_WORKOS_REDIRECT_URI,
          response_type: 'code',
        })

        // Add provider hint for Google/Microsoft
        if (credentials.provider === 'google') {
          params.append('provider', 'GoogleOAuth')
        } else if (credentials.provider === 'microsoft') {
          params.append('provider', 'MicrosoftOAuth')
        }
        
        // For enterprise SSO, we need the organization domain
        if (credentials.provider === 'enterprise' && credentials.email) {
          const domain = credentials.email.split('@')[1]
          params.append('domain', domain)
        }

        // If there's an invite token, add it to the state to preserve it through the SSO flow
        if (credentials.inviteToken) {
          params.append('state', credentials.inviteToken)
        }

        const authUrl = `https://api.workos.com/sso/authorize?${params.toString()}`
        window.location.href = authUrl
      } else if (credentials.method === 'phone') {
        // Handle phone number authentication via your API
        logger.info('Requesting OTP for phone authentication')
        await api.post('/v1/auth/phone/request-otp', {
          phoneNumber: credentials.phoneNumber
        })

        // Return early - user will be redirected to OTP verification
        return
      }
    } catch (error) {
      logger.error('Login failed', { error, method: credentials.method })
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    logger.info('User logging out', { userId: user?.id })
    localStorage.removeItem('auth_token')
    setUser(null)
    setTeam(null)
    setTeams([])
    setNeedsOnboarding(false)
    Sentry.setUser(null)
    window.location.href = '/login'
  }

  const hasUserPermission = (permission: string) => {
    if (!user) return false
    return hasPermission(user.role, permission as any)
  }

  const canAccessTeam = (teamId: string) => {
    if (!user || !team) return false
    return canAccessTeamData(user.role, team.id, teamId)
  }

  const contextValue: AuthContextType = {
    user,
    currentTeam: team,
    team,
    teams,
    loading,
    isAuthenticated: !!user,
    needsOnboarding,
    login,
    logout,
    refreshAuth: checkAuthState,
    hasPermission: hasUserPermission,
    canAccessTeam,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}